import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const Steveston = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.steveston-richmond:About Steveston Paragraph">
          Steveston, nestled at the southwestern tip of{' '}
          <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia
          </LinkText>
          , is a historic fishing village that has gracefully evolved into a
          vibrant community while preserving its rich heritage. Established in
          the 1880s, Steveston was once dubbed &quot;Salmonopolis&quot; due to
          its bustling salmon canning industry. Today, it harmoniously blends
          its storied past with modern attractions, offering visitors and
          residents a unique coastal experience.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$2,100 - $2,500',
          bed2: '$2,700 - $3,100',
          bed3: '$4,195 - $4,595'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.steveston-richmond:Apartment Hunting Steveston Paragraph 1">
            Finding an apartment for rent in Steveston, Richmond, BC requires
            careful planning and consideration. Start by determining your
            criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Steveston lifestyle by exploring your
            new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.steveston-richmond:Apartment Hunting Steveston Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.steveston-richmond:Apartments for rent in Steveston Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Steveston, Richmond
              </LinkText>
              , cater to a diverse demographic, including young professionals,
              retirees, and small families. These units often feature
              contemporary designs, with many offering scenic views of the
              waterfront or village streets. The neighborhood’s blend of
              historic charm and modern amenities, along with its proximity to
              local shops and eateries, makes renting an apartment here
              particularly appealing.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.steveston-richmond:Houses for rent in Steveston Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Steveston, Richmond
              </LinkText>
              , are ideal for families and individuals seeking spacious living
              in a community-rich environment. These homes range from
              heritage-style houses reflecting the area’s history to modern
              builds, all set within walking distance to parks, schools, and the
              picturesque waterfront. The village’s tranquil ambiance combined
              with its community events and festivals offers a wholesome living
              experience.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.steveston-richmond:Townhouses for rent in Steveston Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Steveston, Richmond
              </LinkText>{' '}
              , provide a balanced option for those desiring more space than an
              apartment but with less maintenance than a standalone house. These
              multi-level homes often come with modern amenities and are
              situated close to the village center, allowing residents to enjoy
              the local markets, cafes, and scenic boardwalks with ease.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.steveston-richmond:Basements for rent in Steveston Paragraph">
              Basements for rent in Steveston, Richmond, offer affordable living
              solutions, especially suitable for students or singles. These
              units typically provide private entrances and essential amenities,
              all within the cozy confines of this historic village. Living here
              allows tenants to immerse themselves in the community’s rich
              culture and enjoy the coastal lifestyle.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.steveston-richmond:Condos for rent Steveston Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Steveston, Richmond
              </LinkText>
              , attract those seeking a blend of luxury and convenience. Many
              condominiums boast modern designs, with facilities like fitness
              centers and communal lounges. The proximity to the waterfront,
              coupled with the village’s array of dining and shopping options,
              enhances the appeal of condo living in this area.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.steveston-richmond:Rooms for rent in Steveston Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Steveston, Richmond
              </LinkText>{' '}
              , are ideal for individuals seeking cost-effective accommodations
              within a community setting. Renting a room here offers the chance
              to live in a historic village, participate in local events, and
              enjoy the serene coastal environment, all while maintaining a
              budget-friendly lifestyle.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.steveston-richmond:Short term rentals in Steveston Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Steveston, Richmond
              </LinkText>
              , cater to tourists, business travelers, or those in transition.
              These accommodations range from quaint cottages to modern
              apartments, providing a home-like atmosphere. The village’s
              attractions, such as the Gulf of Georgia Cannery and the scenic
              Fisherman’s Wharf, make it a desirable location for short-term
              stays.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.steveston-richmond:Exploring Steveston Paragraph">
          Steveston is a captivating blend of history and modernity, offering
          visitors a glimpse into its rich past while providing contemporary
          amenities.{' '}
          <LinkText href="https://vancouversbestplaces.com/metro-vancouver/richmond/steveston-village-waterfront-vbps/">
            The village’s waterfront
          </LinkText>{' '}
          is lined with heritage buildings, reflecting its origins as a major
          salmon canning center. Landmarks like the{' '}
          <LinkText href="https://gulfofgeorgiacannery.org/">
            Gulf of Georgia Cannery National Historic Site
          </LinkText>{' '}
          stand as testaments to its industrious past. The community’s
          commitment to preserving its heritage while fostering a vibrant local
          culture makes Steveston a unique destination.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.steveston-richmond:Things To Do Steveston Paragraph">
          Steveston offers a plethora of activities catering to diverse
          interests. History enthusiasts can explore the{' '}
          <LinkText href="https://gulfofgeorgiacannery.org/">
            Gulf of Georgia Cannery National Historic Site
          </LinkText>
          , which provides insights into the village’s canning industry. Nature
          lovers can enjoy{' '}
          <LinkText href="https://www.visitrichmondbc.com/things-to-do/outdoor-activities/?gad_source=1&gclid=Cj0KCQjw-e6-BhDmARIsAOxxlxXGLx3FU2H4Nl5zDFT14k_xxJ2VltfUdrvb-YNYNUdQuClDFL_xSjwaAoStEALw_wcB">
            whale-watching tours
          </LinkText>{' '}
          departing from the harbor, offering sightings of orcas and humpback
          whales.{' '}
          <LinkText href="https://stevestonwaterfronthotel.com/">
            The scenic waterfront
          </LinkText>{' '}
          is perfect for leisurely strolls, with paths leading to{' '}
          <LinkText href="https://www.richmond.ca/parks-recreation/parks/parksearch/park.aspx?ID=17">
            Garry Point Park
          </LinkText>
          , a serene spot ideal for picnics and kite flying. The village also
          hosts seasonal farmers’ markets, showcasing local produce and
          artisanal goods, reflecting the community’s vibrant culture.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.steveston-richmond:Restaurants Steveston Paragraph">
        <Paragraph>
          Steveston Vancouver offers a range of dining options for every taste.
          Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://www.stevestonseafoodhouse.com/">
            Steveston Seafood House
          </LinkText>{' '}
          - Established in 1978, Steveston Seafood House has been a cornerstone
          of the village’s dining scene. Renowned for its fresh, locally sourced
          seafood, the restaurant offers dishes like cedar-planked salmon and
          succulent scallops. The cozy, nautical-themed ambiance provides a warm
          and inviting dining experience, making it a favorite among seafood
          aficionados.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://baanlao.ca/">
            Baan Lao Fine Thai Cuisine
          </LinkText>{' '}
          - An exquisite Thai dining experience, blending traditional flavors
          with contemporary presentation. Chef Nutcha Phanthoupheng crafts
          dishes using locally sourced ingredients, including organic produce
          and sustainable seafood. The elegant setting, overlooking the Fraser
          River, enhances the dining experience, making it ideal for special
          occasions and intimate dinners.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://www.bbco.ca/">
            Britannia Brewing Company
          </LinkText>{' '}
          - Combining craft beer with a delectable menu, Britannia Brewing
          Company is a hub for those seeking a relaxed dining atmosphere. The
          menu features pub classics with a West Coast twist, such as
          beer-battered fish and chips, complemented by their in-house brewed
          ales and lagers.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.steveston-richmond:Transportation Steveston Paragraph">
          Getting around Steveston, Richmond, BC, is convenient with various
          transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Vancouver
          features an extensive network of bike lanes and rental services. Many
          parts of Steveston are also walkable, with key amenities and
          attractions nearby. Drivers will find a mix of street parking and
          parking garages available. No matter your preferred way of getting
          around, Steveston offers accessible and efficient transportation
          choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.steveston-richmond:Where To Stay Steveston Paragraph">
          Accommodation options in Steveston, Richmond cater to those looking
          for a more local and immersive experience. This area offers a mix of
          boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, providing
          travelers with a unique stay. While large hotels may be limited within
          Steveston itself, its proximity to other parts of Vancouver ensures
          that a range of traditional accommodations, from luxury to
          budget-friendly, is just a short distance away. Staying in Steveston
          allows visitors to experience its distinct character while remaining
          conveniently close to the city’s main attractions.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default Steveston
